import { useEffect, useRef, useState } from "react";
import type { ChoiceRequest } from "../api/types";
import type { Theme } from "../theme/pixel";
import { defaultBtn, panelStyle, primaryBtn } from "../theme/styles";
import { playSfx } from "../logic/sfx";
import { useModalFocus } from "../logic/useModalFocus";
import { inertAttrs, inertOnClick } from "../logic/inert";

interface Props {
  request: ChoiceRequest;
  onSubmit: (picks: string[]) => void;
  theme: Theme;
  animating: boolean;
}

// Shown when a card script yields `choose(...)`. Single-pick requests
// submit on click; multi-pick collects until Confirm.
export function ChoiceModal({ request, onSubmit, theme, animating }: Props) {
  const [picks, setPicks] = useState<string[]>([]);
  const singlePick = request.min === 1 && request.max === 1;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset picks when the request changes under us (back-to-back yields).
  useEffect(() => {
    setPicks([]);
  }, [request]);

  // Trap focus inside the choice modal; closing on Escape is a no-op
  // here (no way to cancel a choice without an option), so we only
  // run the focus trap. The choice resolves when the user submits.
  useModalFocus(dialogRef, () => {}, { closeOnEscape: false });

  const toggle = (key: string) => {
    setPicks((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= request.max) {
        playSfx("ai_notice_or_warning_sound");
        return prev;
      }
      return [...prev, key];
    });
  };

  const ready = picks.length >= request.min && picks.length <= request.max;
  const isRange = request.min !== request.max;
  const requirement = isRange ? `${request.min}-${request.max}` : `${request.max}`;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={request.prompt ?? "Choose"}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26, 15, 5, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 90,
      }}
    >
      <div
        style={{
          ...panelStyle(theme),
          padding: "24px 28px",
          minWidth: 360,
          maxWidth: 520,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 14,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          {theme.ornament} {request.prompt ?? "Choose"} {theme.ornament}
        </div>

        {!singlePick && (
          <div
            style={{
              fontFamily: theme.fontBody,
              fontSize: 11,
              color: theme.inkDim,
              textAlign: "center",
            }}
          >
            Picked {picks.length} / {requirement}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {request.options.map((opt) => {
            const selected = picks.includes(opt.key);
            return (
              <button
                type="button"
                key={opt.key}
                {...inertAttrs(animating)}
                onClick={inertOnClick(animating, () => {
                  if (singlePick) {
                    onSubmit([opt.key]);
                  } else {
                    toggle(opt.key);
                  }
                })}
                style={{
                  padding: "10px 14px",
                  fontFamily: theme.fontBody,
                  fontSize: 13,
                  textAlign: "left",
                  color: selected ? theme.bgDeep : theme.ink,
                  background: selected ? theme.accent : theme.panel,
                  border: `2px solid ${selected ? theme.accent : theme.panelEdge}`,
                  borderRadius: theme.radius.button,
                  cursor: animating ? "default" : "pointer",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {!singlePick && (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {request.min === 0 && (
              <button
                type="button"
                {...inertAttrs(animating)}
                onClick={inertOnClick(animating, () => onSubmit([]))}
                {...defaultBtn(theme, animating)}
              >
                NONE
              </button>
            )}
            <button
              type="button"
              {...inertAttrs(!ready || animating)}
              onClick={inertOnClick(!ready || animating, () => onSubmit(picks))}
              {...primaryBtn(theme, !ready || animating)}
            >
              CONFIRM
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
